import { parseSearchFiltersToUrl } from '~/apps/pu-links/infrastructure/searchUrlHelpers'
import { getTransformObject } from '~/common/photos/transforms'
import type { Logger } from '~/composables/useGtmLogRequest'
import { getLangFallbackUrl } from '~/lang/utils/langfetch'
import { getWKTPointFromGeoPoint } from '~/utility/geo/GeoLocation'
import { getPointFromStringOrDefault } from '~/utility/geo/latLng'
import { keysToCamel } from '~/utility/toCamel'
import {
  CampsiteMapsSearchResult,
  MapSearchCampsitesResults,
  SearchFilters,
  campsiteMapPopupSchema,
} from '../schemas'
import { formatBounds } from './bounds'

export class FetchCampsiteMapSearchService {
  async search(
    filters: SearchFilters,
    langCode: string,
    logger: Logger,
    fetchOptions?: any,
  ): Promise<MapSearchCampsitesResults | undefined> {
    const params = this.filtersToParams(filters)
    const fieldsToShow = [
      'cheapest_lead_price',
      'id',
      'lead_price',
      'point',
      'slug',
    ]
    // const gtmLogRequest = useGtmLogRequest(
    //   'FetchCampsiteMapSearchService-search',
    //   logger,
    // )
    // gtmLogRequest.markStart()
    const url = getLangFallbackUrl(
      langCode,
      `/search2/postgres-nonprice/?fields=${fieldsToShow.join(
        ',',
      )}&sort=magic&facet_counts=none&seo=0&${params.toString()}`,
    )

    const response: any = await $fetch(url, fetchOptions)
    // gtmLogRequest.markEnd()
    if (!response) {
      // return nothing early if response is empty, otherwise it will throw an error later on
      return
    }
    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- API response field
      campsites: this.formatResults(keysToCamel(response.results)),
      pagination: {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- API response field
        currentPage: Number(response.page),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
        totalPages: response.num_pages,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
        totalCount: response.count,
      },
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- API response field
      bounds: response.bounding_box
        ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- API response field
          formatBounds(response.bounding_box)
        : null,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      title: response.title,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      heading: response.heading,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- API response field
      bookable: !!response.bookable,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      category: response.category,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      count: response.bookable_count,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      path: response.path,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      point: response.point,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      leadPrice: response.lead_price,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      cheapestLeadPrice: response.cheapest_lead_price,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      sortOptions: response.sort_options,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      sortOption: response.sort || response.sort_field,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      maxPrice: response.max_price,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      within: response.within,
    }
  }

  private formatResults(results: any[]): CampsiteMapsSearchResult[] {
    return results.map((campsite) => {
      const point = getWKTPointFromGeoPoint(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- API response field
        getPointFromStringOrDefault(campsite.point),
      )
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      const { lat, lng } = campsite.point
      return {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- API response field
        id: campsite.id.toString(),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
        slug: campsite.slug,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
        price: campsite.cheapestLeadPrice
          ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- API response field
            campsite.cheapestLeadPrice[0]
          : undefined,
        point,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- API response field
        lat,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- API response field
        lng,
      }
    })
  }

  // tslint:disable-next-line cognitive-complexity
  private filtersToParams(filters: SearchFilters) {
    const params = new URLSearchParams([])
    for (const param of parseSearchFiltersToUrl({
      ...filters,
      // limit: SEARCH_RESULTS_LIMIT,
    })) {
      params.append(param.filterName, param.filterValue)
    }
    if (params.get('bounds')) {
      // remove bounds if path
      if (params.get('path')) {
        params.delete('bounds')
      }
    }
    // only full bookable
    params.append('bookable', '1')
    return params
  }

  async fetchCampsiteBySlug(slug, langCode, logger: Logger) {
    const gtmLogRequest = useGtmLogRequest(
      'FetchCampsiteMapSearchService-fetchCampsiteBySlug',
      logger,
    )
    gtmLogRequest.markStart()

    const campsite: any = await $fetch(
      getLangFallbackUrl(langCode, `/_/campsite-details/slim/${slug}/`),
    )
    gtmLogRequest.markEnd()
    return campsiteMapPopupSchema.parse({
      ...campsite,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      bookable: campsite.isBookable,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      hierarchy: campsite.hierarchyTextShort,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- API response field
      photo: campsite.primaryPhoto
        ? {
            url: getTransformObject(
              'map',
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- API response field
              campsite.primaryPhoto.url.master_image,
            ),
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
            caption: campsite.primaryPhoto?.caption || '',
          }
        : null,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      price: campsite.leadPrice ? campsite.leadPrice : undefined,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- API response field
      status: campsite.isBookable ? 'bookable' : 'free',
    })
  }
}

export function getCampsiteBySlug(slug, langCode, logger: Logger) {
  const service = new FetchCampsiteMapSearchService()
  return service.fetchCampsiteBySlug(slug, langCode, logger)
}
